import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import Logout from './components/Logout';
import Oops from './components/Oops';
import PastBroadcasts from './components/PastBroadcasts';
import Admin from './containers/Admin';
import Analytics from './containers/Analytics';
import Apps from './containers/Apps';
import BroadcastDetails from './containers/BroadcastDetails';
import Channels from './containers/Channels';
import ConnectionManager from './containers/ConnectionManager';
import CreateAccount from './containers/CreateAccount';
import CreatePassword from './containers/CreatePassword';
import Dashboard from './containers/Dashboard';
import Engagement from './containers/Engagement';
import JoinBroadcast from './containers/JoinBroadcast';
import Login from './containers/Login';
import LoginSSO from './containers/LoginSSO';
import Player from './containers/Player';
import Players from './containers/Players';
import Recordings from './containers/Recordings';
import ResetPassword from './containers/ResetPassword';
import Space from './containers/Space';
import User from './containers/User';
import Users from './containers/Users';

import { Pathnames } from './helpers/enums';
import { broadcastsDataShape, channelItemShape } from './helpers/propTypeShapes';

export default function Routes({
  accountId,
  accountRole,
  amIASuperAdmin,
  APIBaseURL,
  appLogin,
  broadcastsData,
  channels,
  checkAuthorization,
  clearGlobalAccount,
  componentReady,
  currentYear,
  getCookieValue,
  getPrimaryKey,
  getScreenOrientation,
  handleLogout,
  loadBroadcasts,
  loadChannels,
  reAuth,
  setMobileDevice,
  setGlobalAccountData,
  setGlobalAccountId,
  setGlobalAccountName,
  setGlobalAccountRole,
  setGlobalUserData,
  showHeader,
  switchAccount,
  throttle,
  trackEvent,
}) {
  return (
    <Switch>
      <Route
        path={['/', Pathnames.LOGIN]}
        exact
        render={() => (
          <Login
            APIBaseURL={APIBaseURL}
            appLogin={appLogin}
            checkAuthorization={checkAuthorization}
            currentYear={currentYear}
            setMobileDevice={setMobileDevice}
            showHeader={showHeader}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path={['/login-sso/:ssoId', '/login-sso']}
        render={() => (
          <LoginSSO checkAuthorization={checkAuthorization} showHeader={showHeader} trackEvent={trackEvent} />
        )}
      />
      <Route
        path="/admin/:pageName"
        render={({ location, match }) => (
          <Admin
            checkAuthorization={checkAuthorization}
            location={location}
            match={match}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path="/analytics/:pageName"
        render={({ location, match, history }) => (
          <Analytics
            checkAuthorization={checkAuthorization}
            history={history}
            location={location}
            match={match}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path={Pathnames.APPS}
        render={({ location, match }) => (
          <Apps
            checkAuthorization={checkAuthorization}
            location={location}
            match={match}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path="/broadcastdetails/:streamOrBroadcast/:hash/:id/:deeplink"
        render={({ match, history, location }) => (
          <BroadcastDetails
            checkAuthorization={checkAuthorization}
            componentReady={componentReady}
            getPrimaryKey={getPrimaryKey}
            history={history}
            location={location}
            match={match}
            reAuth={reAuth}
            switchAccount={switchAccount}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path={Pathnames.CHANNELS}
        render={({ history, location }) => (
          <Channels
            accountId={accountId}
            APIBaseURL={APIBaseURL}
            checkAuthorization={checkAuthorization}
            getPrimaryKey={getPrimaryKey}
            history={history}
            location={location}
            reAuth={reAuth}
          />
        )}
      />
      <Route path="/space/:spaceToken" render={() => <Space checkAuthorization={checkAuthorization} />} />
      <Route
        path={Pathnames.CONNECTION_MANAGER}
        render={() => (
          <ConnectionManager APIBaseURL={APIBaseURL} accountId={accountId} checkAuthorization={checkAuthorization} />
        )}
      />
      <Route
        path={Pathnames.CREATEACCOUNT}
        render={({ location }) => (
          <CreateAccount
            APIBaseURL={APIBaseURL}
            appLogin={appLogin}
            currentYear={currentYear}
            location={location}
            showHeader={showHeader}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path={Pathnames.CREATEPASSWORD}
        render={({ history, location }) => (
          <CreatePassword
            APIBaseURL={APIBaseURL}
            currentYear={currentYear}
            history={history}
            location={location}
            showHeader={showHeader}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path={['/join/:broadcastHash/:guestHash', '/join/:broadcastHash']}
        render={() => (
          <JoinBroadcast checkAuthorization={checkAuthorization} trackEvent={trackEvent} appLogin={appLogin} />
        )}
      />
      <Route
        path={[Pathnames.DASHBOARD, Pathnames.JOIN]}
        render={({ history, location }) => (
          <Dashboard
            accountId={accountId}
            APIBaseURL={APIBaseURL}
            broadcastsData={broadcastsData}
            channels={channels}
            checkAuthorization={checkAuthorization}
            componentReady={componentReady}
            getCookieValue={getCookieValue}
            history={history}
            loadBroadcasts={loadBroadcasts}
            location={location}
            reAuth={reAuth}
            setGlobalAccountData={setGlobalAccountData}
            setGlobalAccountId={setGlobalAccountId}
            setGlobalAccountName={setGlobalAccountName}
            setGlobalAccountRole={setGlobalAccountRole}
            setGlobalUserData={setGlobalUserData}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path="/engagement/:pageName"
        render={({ history, match }) => (
          <Engagement
            checkAuthorization={checkAuthorization}
            history={history}
            match={match}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route path={Pathnames.LOGOUT} render={() => <Logout handleLogout={handleLogout} />} />
      <Route
        path={Pathnames.PLAYERS}
        render={({ history, location }) => (
          <Players
            accountId={accountId}
            APIBaseURL={APIBaseURL}
            checkAuthorization={checkAuthorization}
            getPrimaryKey={getPrimaryKey}
            history={history}
            location={location}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path="/player/:id"
        render={({ history, location, match }) => (
          <Player APIBaseURL={APIBaseURL} history={history} location={location} match={match} reAuth={reAuth} />
        )}
      />
      <Route
        path={Pathnames.RECORDINGS}
        render={() => (
          <Recordings
            accountId={accountId}
            channels={channels}
            checkAuthorization={checkAuthorization}
            componentReady={componentReady}
            getScreenOrientation={getScreenOrientation}
            loadBroadcasts={loadBroadcasts}
            reAuth={reAuth}
            trackEvent={trackEvent}
            loadChannels={loadChannels}
          />
        )}
      />
      <Route
        path={Pathnames.RESETPASSWORD}
        render={({ location }) => (
          <ResetPassword
            APIBaseURL={APIBaseURL}
            currentYear={currentYear}
            location={location}
            showHeader={showHeader}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path="/user/:userId"
        render={({ history, location, match }) => (
          <User
            accountId={accountId}
            APIBaseURL={APIBaseURL}
            history={history}
            location={location}
            match={match}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        )}
      />
      <Route
        path={Pathnames.USERS}
        render={() => (
          <Users
            APIBaseURL={APIBaseURL}
            accountId={accountId}
            checkAuthorization={checkAuthorization}
            reAuth={reAuth}
          />
        )}
      />
      <Route
        path="/webview/recordings"
        render={() => (
          <PastBroadcasts
            amIASuperAdmin={amIASuperAdmin}
            appLogin={appLogin}
            getScreenOrientation={getScreenOrientation}
            reAuth={reAuth}
            showHeader={showHeader}
            trackEvent={trackEvent}
            isWebview
          />
        )}
      />
      <Route component={Oops} />
    </Switch>
  );
}

Routes.defaultProps = {
  accountId: null,
  accountRole: null,
};

Routes.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accountRole: PropTypes.string,
  APIBaseURL: PropTypes.string.isRequired,
  appLogin: PropTypes.func.isRequired,
  amIASuperAdmin: PropTypes.bool.isRequired,
  broadcastsData: broadcastsDataShape.isRequired,
  channels: PropTypes.arrayOf(channelItemShape).isRequired,
  checkAuthorization: PropTypes.func.isRequired,
  clearGlobalAccount: PropTypes.func.isRequired,
  componentReady: PropTypes.bool.isRequired,
  currentYear: PropTypes.number.isRequired,
  getCookieValue: PropTypes.func.isRequired,
  getPrimaryKey: PropTypes.func.isRequired,
  getScreenOrientation: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  loadBroadcasts: PropTypes.func.isRequired,
  reAuth: PropTypes.func.isRequired,
  setMobileDevice: PropTypes.func.isRequired,
  setGlobalAccountData: PropTypes.func.isRequired,
  setGlobalAccountId: PropTypes.func.isRequired,
  setGlobalAccountName: PropTypes.func.isRequired,
  setGlobalAccountRole: PropTypes.func.isRequired,
  setGlobalUserData: PropTypes.func.isRequired,
  showHeader: PropTypes.func.isRequired,
  switchAccount: PropTypes.func.isRequired,
  throttle: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  loadChannels: PropTypes.func.isRequired,
};
